import React from "react";
import PropTypes from "prop-types";

/**
 * Resolves badge color, background color, and border color from a base status color.
 */
const resolveBadgeColors = (color, bgColor, borderColor) => {
    if (bgColor && borderColor) {
        return { color: color || "#475569", bgColor, borderColor };
    }

    let hex = typeof color === "string" ? color.trim() : "";
    if (hex.startsWith("#")) {
        if (hex.length === 4) {
            hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
        }
        if (hex.length === 7) {
            return {
                color: hex,
                bgColor: bgColor || `${hex}18`,
                borderColor: borderColor || `${hex}40`,
            };
        }
        if (hex.length === 9) {
            return {
                color: hex.slice(0, 7),
                bgColor: bgColor || `${hex.slice(0, 7)}18`,
                borderColor: borderColor || `${hex.slice(0, 7)}40`,
            };
        }
    }

    return {
        color: color || "#475569",
        bgColor: bgColor || "#f1f5f9",
        borderColor: borderColor || "#cbd5e1",
    };
};

/**
 * Shared Status Badge pill component.
 * Displays the status string returned directly from the API with its corresponding statusColor.
 *
 * @param {Object} props
 * @param {string} [props.status] - Status text returned from API (e.g. "Semen Dose Status Active")
 * @param {string} [props.statusColor] - Status hex color returned from API (e.g. "#198754")
 * @param {Object} [props.item] - Optional row item containing status and statusColor
 * @param {string} [props.color] - Custom text color override
 * @param {string} [props.bgColor] - Custom background color override
 * @param {string} [props.borderColor] - Custom border color override
 * @param {Object} [props.style] - Custom inline styles
 * @param {string} [props.className] - Custom CSS class
 */
export default function StatusBadge({
    status,
    statusColor,
    item,
    color,
    bgColor,
    borderColor,
    style = {},
    className = "",
}) {
    const rawStatus = status || item?.status || "";
    const rawColor = color || statusColor || item?.statusColor || "#475569";

    const colors = resolveBadgeColors(rawColor, bgColor, borderColor);

    return (
        <span
            className={className}
            style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: 12,
                backgroundColor: colors.bgColor,
                color: colors.color,
                border: colors.borderColor ? `1px solid ${colors.borderColor}` : "none",
                fontSize: 12,
                fontWeight: 600,
                whiteSpace: "nowrap",
                ...style,
            }}
        >
            {rawStatus || "-"}
        </span>
    );
}

StatusBadge.propTypes = {
    status: PropTypes.string,
    statusColor: PropTypes.string,
    item: PropTypes.object,
    color: PropTypes.string,
    bgColor: PropTypes.string,
    borderColor: PropTypes.string,
    style: PropTypes.object,
    className: PropTypes.string,
};
